import { useState } from 'react';
import { Link } from '@inertiajs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faCheck } from '@fortawesome/free-solid-svg-icons';
import RecordSelector from '../../../../components/RecordSelector';
import AttachmentPreview from '../../Attachments/AttachmentPreview';
import UploadModal from '../../Attachments/NewAttachmentForm/UploadModal';
import { formatDateTime } from '../../../Announcements/dates';

interface ImageSelectorProps {
  name: string;
  isMulti?: boolean;
}

export default function ImageSelector({ name, isMulti = false }: ImageSelectorProps) {
  const [editingAttachment, setEditingAttachment] = useState<any | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const formatOptionLabel = (option: any, meta: any, onSelect: (o: any) => void) => {
    const { context, selectValue } = meta;

    if (context === 'value') {
      return (
        <div className="flex items-center gap-2">
          {option.public_url && (
            <img src={option.public_url} alt={option.label} className="w-6 h-6 object-cover flex-shrink-0" />
          )}
          <span className="text-sm truncate">{option.label || option.filename}</span>
        </div>
      );
    }

    const isSelected = Array.isArray(selectValue)
      ? selectValue.some((v: any) => v.value === option.value)
      : selectValue?.value === option.value;

    return (
      <div className="flex items-center gap-2 w-full">
        {option.public_url ? (
          <img src={option.public_url} alt={option.label} className="w-10 h-10 object-cover flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 bg-base-300 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{option.label || option.filename}</div>
          <div className="text-xs text-base-content/60 flex gap-2 flex-wrap">
            {option.filename && <span>{option.filename}</span>}
            {option.human_readable_size && <span>{option.human_readable_size}</span>}
            {option.created_at && <span>{formatDateTime(option.created_at)}</span>}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="btn btn-xs btn-ghost"
            title="Edit"
            onClick={() => setEditingAttachment(option)}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          {!isSelected && (
            <button
              type="button"
              className="btn btn-xs btn-ghost"
              title="Select"
              onClick={() => onSelect(option)}
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {editingAttachment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setEditingAttachment(null); }}
        >
          <div className="shadow-xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ maxWidth: 560, backgroundColor: '#f5f5f5' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Edit attachment</span>
              <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={() => setEditingAttachment(null)}>✕</button>
            </div>
            <div style={{ padding: '8px' }}>
              <AttachmentPreview serverData={editingAttachment} key={`edit-${editingAttachment.value}`} />
            </div>
          </div>
        </div>
      )}

      <RecordSelector
        name={name}
        isMulti={isMulti}
        searchUrl="/admin/attachments/search"
        label={isMulti ? 'Attachments' : 'Attachment'}
        formatOptionLabel={formatOptionLabel}
        footer={(field) => (
          <>
            {showUploadModal && (
              <UploadModal
                onUploaded={(options) => {
                  setShowUploadModal(false);
                  if (!options.length) return;
                  const next = isMulti
                    ? [...(field.value || []), ...options]
                    : options[0];
                  field.onChange(next);
                }}
                onClose={() => setShowUploadModal(false)}
              />
            )}
            <div className="flex items-center gap-3 mt-1">
              <Link href="/admin/attachments" className="link link-primary text-xs">
                Manage attachments
              </Link>
              <button
                type="button"
                className="link link-primary text-xs"
                onClick={() => setShowUploadModal(true)}
              >
                + Upload new
              </button>
            </div>
          </>
        )}
      />
    </>
  );
}
